// @ts-check

import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Text, DataTable, Box, InlineStack, Button, Spinner, Link } from "@shopify/polaris";
import { SearchResourceIcon } from "@shopify/polaris-icons";

import { format } from "date-fns";

import { TruncatedTableUTMValue } from "components/TruncatedTableUTMValue/TruncatedTableUTMValue";

import { useFormatCurrency } from "hooks/use-format-currency";

const NUM_RESULTS = 5;

const createPlaceholderResults = (numResults) => {
  return Array.from({ length: numResults }, (v, i) => ([
    "-",
    "-",
    "-",
    "-",
    "-",
    "-",
    "-",
    "-",
  ]));
};

export const MostRecentOrdersCard = ({
  metricName,
  metricKey,
  metricValue,
  data = [],
  totalCount = 0,
  loading = false,
}) => {
  const formatCurrency = useFormatCurrency();
  const navigate = useNavigate();

  const handleViewMore = () => {
    const params = {
      [metricKey]: metricValue,
    };

    navigate(`/orders?${new URLSearchParams(params).toString()}`);
  };

  const handleOrderClick = (graphqlOrderId) => {
    const orderId = graphqlOrderId.split("Order/")[1];
    open(`shopify://admin/orders/${orderId}`, "_top");
  };

  const tableData = useMemo(() => {
    if (loading) {
      return createPlaceholderResults(NUM_RESULTS);
    }

    const realData = data.map((order) => {
      const link = (
        <Link
          removeUnderline
          onClick={() => handleOrderClick(order.order_id)}
        >
          <Text variant="bodyMd" as="span">{order.name}</Text>
        </Link>
      );

      const amount = (
        <Text variant="bodyMd" as="span" fontWeight="semibold" alignment="end" numeric>
          {formatCurrency(order.shop_money_order_amount, {
            currency: order.shop_money_currency_code,
          })}
        </Text>
      );
      
      return [
        link,
        format(new Date(order.order_created_at), "MMM d',' h':'mmaaa"),
        <TruncatedTableUTMValue
          value={order.utm_source}
          calculateReportHref={(value) => `/reports/source/${encodeURIComponent(value)}`}
        />,
        <TruncatedTableUTMValue
          value={order.utm_medium}
          calculateReportHref={(value) => `/reports/medium/${encodeURIComponent(value)}`}
        />,
        <TruncatedTableUTMValue
          value={order.utm_campaign}
          calculateReportHref={(value) => `/reports/campaign/${encodeURIComponent(value)}`}
        />,
        <TruncatedTableUTMValue value={order.utm_content} />,
        <TruncatedTableUTMValue value={order.utm_term} />,
        amount,
      ];
    });

    const placeholderData = createPlaceholderResults(NUM_RESULTS - realData.length);

    return [...realData, ...placeholderData];
  }, [loading, data]);

  let footer;
  if (loading) {
    footer = "Loading...";
  } else {
    footer = `Showing ${data.length} of ${totalCount} results`;
  }

  let spinner;
  if (loading) {
    spinner = <Spinner size="small" />;
  }

  return (
    <Card padding="0">
      <Box padding="300">
        <InlineStack align="space-between">
          <Text as="h2" variant="headingSm">Most recent orders for this {metricName} (during this time period)</Text>

          <InlineStack align="space-between" gap="200">
            {spinner}

            <Button
              icon={SearchResourceIcon}
              variant="tertiary"
              onClick={handleViewMore}
              accessibilityLabel="View more"
            >
              View all
            </Button>
          </InlineStack>
        </InlineStack>
      </Box>

      <DataTable
        columnContentTypes={[
          "text",
          "text",
          "text",
          "text",
          "text",
          "text",
          "text",
          "numeric",
        ]}
        headings={[
          "Order #",
          "Date",
          "UTM Source",
          "UTM Medium",
          "UTM Campaign",
          "UTM Content",
          "UTM Term",
          "Amount",
        ]}
        rows={tableData}
        footerContent={footer}
      />
    </Card>
  );
};
